import { useCallback } from 'react'
import { twMerge } from 'tailwind-merge'
import {
  AskUserQuestionCard,
  buildQuestionDismissal,
  type AskUserQuestionAnswer,
} from '@renderer/chat/integrations/ask-user-question'
import { FLOATING_SURFACE_BORDER_CLASS, FLOATING_SURFACE_STYLE } from '@renderer/components/floating-surface'
import { useAnswerActionQuestions } from '../../../hooks/use-answer-action-questions'
import { useFloatingBarContentMaxHeight } from '../../../hooks/use-floating-bar-content-max-height'
import type { QuestionsPrerequisite } from '../../../hooks/staged-actions/types'
import { BusyPill } from './busy-pill'

/** Takes the Ready controls' place above the chat input until the action's questions are answered or skipped. */
export function ActionQuestionsCard({ actionId, questions }: { actionId: string; questions: QuestionsPrerequisite }) {
  const { answerQuestions, isAnswering } = useAnswerActionQuestions()
  const maxHeight = useFloatingBarContentMaxHeight()

  const handleSubmit = useCallback(
    (answer: AskUserQuestionAnswer) => answerQuestions(actionId, answer),
    [actionId, answerQuestions],
  )
  const handleSkip = useCallback(() => {
    answerQuestions(actionId, buildQuestionDismissal(questions.questions)).catch(console.error)
  }, [actionId, answerQuestions, questions.questions])

  return (
    <div
      className={twMerge(
        FLOATING_SURFACE_BORDER_CLASS,
        'corner-smooth flex min-w-0 flex-1 flex-col gap-3 rounded-2xl px-3 pb-3 pt-2',
      )}
      style={{ ...FLOATING_SURFACE_STYLE, maxHeight }}
    >
      {isAnswering && <BusyPill label="Applying your answers…" />}
      <div className={twMerge('flex min-h-0 flex-col gap-3', isAnswering && 'pointer-events-none opacity-40')}>
        {questions.talkingPoints.length > 0 && (
          <div className="flex flex-col gap-1">
            {questions.talkingPointsLead && (
              <p className="text-foreground-secondary type-ui-sm">{questions.talkingPointsLead}</p>
            )}
            <ul className="text-foreground type-ui-sm list-disc pl-4">
              {questions.talkingPoints.map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
          </div>
        )}
        <AskUserQuestionCard
          input={{ intro: questions.intro, questions: questions.questions }}
          onSubmit={handleSubmit}
          onDismiss={handleSkip}
          bare
          scrollableBody
        />
      </div>
    </div>
  )
}
